import { Box, Card, CardBody, Flex, Icon, Stack, Text } from "@chakra-ui/react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { FiClock } from "react-icons/fi";
import { PortalLifecycleStatus, type PortalSourcingEventDetailDto } from "../../../../autogen/bff-api";
import { momentLocaleFor } from "../../../../common/formatting/i18nFormatters";
import { formatShortDate } from "../utils/displayFormatters";
import { effectiveDeadlineIso, isRequestPhase } from "../utils/effectiveDeadline";

export const EventDeadlineHighlight = ({ event }: { event: PortalSourcingEventDetailDto }) => {
  const { t, i18n } = useTranslation();
  const locale = momentLocaleFor(i18n.language);
  const deadlineIso = effectiveDeadlineIso(event);
  const deadline = moment(deadlineIso);

  // The live countdown only makes sense while the competition is open; other states are conveyed by the lifecycle banner.
  if (event.lifecycle.status !== PortalLifecycleStatus.Open || deadlineIso == null) return null;

  const daysLeft = Math.ceil(deadline.diff(moment(), "hours") / 24);

  return (
    <Card variant="outline" rounded="xl" bg="smPrimarySofter" borderColor="smPrimarySoft">
      <CardBody px={{ base: "4", md: "6" }} py={{ base: "5", md: "5" }}>
        <Flex align="center" justify="space-between" gap="4">
          <Flex align="center" gap="4">
            <Flex
              width={{ base: "44px", md: "52px" }}
              height={{ base: "44px", md: "52px" }}
              rounded="full"
              bg="smBackground"
              align="center"
              justify="center"
              flexShrink="0"
            >
              <Icon as={FiClock} boxSize="6" color="smPrimary" />
            </Flex>
            <Stack spacing="0">
              <Text
                fontSize="xs"
                fontWeight="semibold"
                color="smPrimary"
                textTransform="uppercase"
                letterSpacing="wide"
              >
                {isRequestPhase(event) ? t("Deadline for requests to participate") : t("Bid deadline")}
              </Text>
              <Text fontSize={{ base: "md", md: "lg" }} fontWeight="semibold" color="smText">
                {formatShortDate(deadlineIso, locale)} {t("at_time")} {deadline.format("HH:mm")}
              </Text>
            </Stack>
          </Flex>

          <Box textAlign="right">
            <Text fontSize={{ base: "4xl", md: "5xl" }} fontWeight="bold" color="smPrimary" lineHeight="1">
              {daysLeft}
            </Text>
            <Text
              fontSize="xs"
              fontWeight="semibold"
              color="smPrimary"
              textTransform="uppercase"
              letterSpacing="wide"
              mt="1"
            >
              {t("days_left", { count: daysLeft })}
            </Text>
          </Box>
        </Flex>
      </CardBody>
    </Card>
  );
};
